import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Download, Landmark, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { downloadFile } from '@/lib/download'
import { PeriodFilter } from '@/features/finance/components/period-filter'
import { StatCards } from '@/features/finance/components/stat-cards'
import { PnLStatement } from '@/features/finance/components/pnl-statement'
import { CategoryPie, TrendChart } from '@/features/finance/components/finance-charts'
import { TransactionsTab } from '@/features/finance/components/transactions-tab'
import { InvoicesTab } from '@/features/finance/components/invoices-tab'
import { FloorplanSummary } from '@/features/finance/components/floorplan-summary'
import {
  PERIOD_PRESETS,
  presetPeriod,
  priorPeriod,
  type PeriodPreset,
} from '@/features/finance/period'
import { usePnL, useTrend } from '@/features/finance/hooks'
import { requireFlow } from '@/features/auth/guards'

const TABS = ['pnl', 'transactions', 'invoices', 'floorplan'] as const
type FinanceTab = (typeof TABS)[number]

interface FinanceSearch {
  tab?: FinanceTab
  preset?: PeriodPreset
  from?: string
  to?: string
}

export const Route = createFileRoute('/_authenticated/finance/')({
  beforeLoad: requireFlow('finance'),
  validateSearch: (search: Record<string, unknown>): FinanceSearch => ({
    tab: TABS.includes(search.tab as FinanceTab) ? (search.tab as FinanceTab) : undefined,
    preset: PERIOD_PRESETS.includes(search.preset as PeriodPreset)
      ? (search.preset as PeriodPreset)
      : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
  }),
  component: FinancePage,
})

function FinancePage() {
  const navigate = useNavigate()
  const { tab, preset: presetParam, from: fromParam, to: toParam } = Route.useSearch()

  const activeTab = tab ?? 'pnl'
  const preset: PeriodPreset = presetParam ?? 'this_year'

  // With a preset, the dates are derived (so "This month" stays correct as
  // months roll over); "custom" keeps whatever explicit range is in the URL.
  const defaultPeriod = presetPeriod(preset)
  const from = preset === 'custom' ? (fromParam ?? defaultPeriod.from) : defaultPeriod.from
  const to = preset === 'custom' ? (toParam ?? defaultPeriod.to) : defaultPeriod.to

  // Search state is rebuilt wholesale (never spread from `prev`) so params
  // from other routes can't leak into the finance route's typed search.
  const setSearch = (patch: FinanceSearch) =>
    navigate({
      to: '/finance',
      search: {
        tab: patch.tab ?? activeTab,
        preset: patch.preset ?? (patch.from || patch.to ? 'custom' : preset),
        from: patch.from ?? (preset === 'custom' ? from : undefined),
        to: patch.to ?? (preset === 'custom' ? to : undefined),
      },
    })

  const { data: pnl, isLoading } = usePnL(from, to)
  const prior = priorPeriod({ from, to })
  const { data: priorPnl, isLoading: priorLoading } = usePnL(prior.from, prior.to)
  const { data: trend, isLoading: trendLoading } = useTrend(12)

  const exportPnL = () =>
    downloadFile('/finance/pnl/export', {
      params: { from, to },
      filename: `pnl-${from}_${to}.csv`,
    })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Landmark className="h-8 w-8" /> Finance
          </h1>
          <p className="text-muted-foreground">
            Profit &amp; loss statements and the transactions ledger.
          </p>
        </div>
        <PeriodFilter
          preset={preset}
          from={from}
          to={to}
          onPresetChange={(p) => setSearch({ preset: p, ...(p === 'custom' ? { from, to } : {}) })}
          onFromChange={(v) => setSearch({ preset: 'custom', from: v, to })}
          onToChange={(v) => setSearch({ preset: 'custom', from, to: v })}
        />
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setSearch({ tab: v as FinanceTab })}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="pnl">P&amp;L Statement</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="floorplan">Floor Plan</TabsTrigger>
          </TabsList>
          {activeTab === 'pnl' && (
            <Button variant="outline" size="sm" onClick={exportPnL}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          )}
        </div>

        <TabsContent value="pnl" className="mt-4 flex flex-col gap-4">
          <StatCards pnl={pnl} priorPnl={priorPnl} isLoading={isLoading} />
          <PnLStatement pnl={pnl} priorPnl={priorPnl} isLoading={isLoading || priorLoading} />
          <div className="grid gap-4 lg:grid-cols-2">
            <TrendChart trend={trend} isLoading={trendLoading} />
            <CategoryPie pnl={pnl} isLoading={isLoading} kind="revenue" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <CategoryPie pnl={pnl} isLoading={isLoading} kind="expenses" />
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <TransactionsTab from={from} to={to} />
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <InvoicesTab />
        </TabsContent>

        <TabsContent value="floorplan" className="mt-4">
          <FloorplanSummary />
        </TabsContent>
      </Tabs>

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <RefreshCw className="h-3 w-3" />
        Won deals, consignment commissions, boat sales, work-order costs, storage fees, floor plan
        curtailments and floor plan interest are recorded automatically; the Add-entry button
        covers everything else.
      </p>
    </div>
  )
}
